import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, ShieldCheck } from "lucide-react";
import { DataList, PageHeader, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Button, Eyebrow, Panel, Section, StateView, cn, useAuth, useToast } from "@ozpanel/ui";

// ConfigCheckScreen — the platform's own configuration self-check, surfaced.
//
// internal/confcheck has always compared what the DAEMONS are actually doing against what
// this platform requires, with an operator-facing consequence for every requirement, and it
// reads master.cf per-service overrides where the global would mislead. It was reachable only
// by curl. A self-check nobody sees is a self-check that catches nothing — which is precisely
// how a missing sender-login restriction sat unnoticed on a live pair.

interface Item {
  service: string;
  key: string;
  want: string;
  got: string;
  grade: string;
  from: string;
  ok: boolean;
  // `consequence`, not `because`. Declared under the wrong name it was optional, always absent,
  // and never rendered — so the one field whose own comment calls it "the part that makes a check
  // actionable rather than a red dot" has never once appeared on the page.
  consequence?: string;
  optional?: boolean;
}

// THREE outcomes, never two. "checked and fine", "checked and wrong", and "could not check"
// are different facts and the backend now reports them distinctly (ok=false + grade=unknown
// means unmeasured, and is deliberately not counted as a failure).
const unmeasured = (i: Item) => !i.ok && i.grade === "unknown";
const outcome = (i: Item) => (i.ok ? i.grade : unmeasured(i) ? "unmeasured" : "failing");
const gradeTone = (i: Item) =>
  i.ok ? (i.grade === "effective" ? "ok" : "warn") : unmeasured(i) ? "neutral" : "bad";

export function ConfigCheckScreen() {
  const { client } = useAuth();
  const [openKey, setOpenKey] = React.useState<string | null>(null);
  const [failsOnly, setFailsOnly] = React.useState(true);

  const check = useQuery({
    queryKey: ["config-check"],
    queryFn: () => client.apiFetch<{ items: Item[]; note?: string; summary?: Record<string, unknown> }>("/ozpanel/config-check"),
    // Every requirement shells out to a real tool; this is deliberately not polled.
    staleTime: 60_000,
  });

  const items = check.data?.items ?? [];
  const failing = items.filter((i) => !i.ok && !unmeasured(i));
  const unknown = items.filter(unmeasured);
  const shown = failsOnly ? [...failing, ...unknown] : items;

  const byService = React.useMemo(() => {
    const m = new Map<string, Item[]>();
    for (const i of shown) m.set(i.service, [...(m.get(i.service) ?? []), i]);
    return [...m.entries()].sort((a, b) => {
      const af = a[1].filter((i) => !i.ok).length, bf = b[1].filter((i) => !i.ok).length;
      return bf - af || a[0].localeCompare(b[0]); // worst first
    });
  }, [shown]);

  return (
    <div>
      <PageHeader
        title="Configuration self-check"
        subtitle="What the running services are actually doing, measured against what this platform requires."
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => setFailsOnly((v) => !v)}>
              {failsOnly ? `Show all ${items.length}` : `Needs attention (${failing.length + unknown.length})`}
            </Button>
            <Button size="sm" variant="ghost" disabled={check.isFetching} onClick={() => check.refetch()}>
              {check.isFetching ? "Checking…" : "Re-run"}
            </Button>
          </div>
        }
      />

      <StateView
        loading={check.isLoading}
        error={check.error}
        errorContext="the configuration self-check"
        empty={!check.isLoading && shown.length === 0}
        emptyMessage={failsOnly ? "Every requirement was measured and satisfied." : "No requirements returned."}
        emptyHint={failsOnly ? `${items.length} checks ran against the live services.` : undefined}
      >
        <div className="mb-4 flex items-center gap-3 rounded-card border border-line bg-surface px-4 py-3">
          <ShieldCheck size={16} className={failing.length ? "text-warn" : "text-ok"} />
          <span className="text-[13.5px]">
            <span className="font-semibold">{items.length - failing.length}</span> of{" "}
            <span className="font-semibold">{items.length}</span> requirements satisfied
          </span>
          {failing.length > 0 && <StatusPill tone="bad">{failing.length} failing</StatusPill>}
          {unknown.length > 0 && <StatusPill tone="neutral">{unknown.length} unmeasured</StatusPill>}
        </div>

        <div className="space-y-4">
          {byService.map(([service, list]) => (
            <Panel key={service} className="overflow-hidden">
              <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
                <Eyebrow accent>{service}</Eyebrow>
                <span className="font-mono text-[11px] text-faint">
                  {list.filter((i) => i.ok).length}/{list.length}
                </span>
              </div>
              <div className="divide-y divide-line">
                {list.map((i) => {
                  const id = `${i.service}:${i.key}`;
                  const open = openKey === id;
                  return (
                    <div key={id}>
                      <button
                        type="button"
                        onClick={() => setOpenKey(open ? null : id)}
                        className="flex w-full items-start gap-3 px-4 py-2.5 text-left hover:bg-surface2"
                      >
                        <ChevronRight
                          size={12}
                          className={cn("mt-1 shrink-0 text-faint transition-transform", open && "rotate-90")}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="font-mono text-[12.5px]">{i.key}</div>
                          <div className="mt-0.5 text-[12px] text-muted">
                            wants <span className="text-fg">{i.want}</span>
                            {" · got "}
                            <span className={i.ok ? "text-ok" : "text-crit"}>{i.got || "(nothing)"}</span>
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-1.5">
                          {i.optional && <span className="font-mono text-[10px] text-faint">optional</span>}
                          <StatusPill tone={gradeTone(i)}>{outcome(i)}</StatusPill>
                        </div>
                      </button>
                      {open && (
                        <div className="space-y-2 border-t border-line bg-bg2 px-4 py-3 pl-9">
                          {/* The consequence, in the operator's terms — this is the part that
                              makes a check actionable rather than a red dot. */}
                          {i.consequence && (
                            <p className="text-[12.5px] leading-relaxed text-muted">{i.consequence}</p>
                          )}
                          <div>
                            <Eyebrow className="mb-1">Measured by</Eyebrow>
                            <code className="block overflow-x-auto rounded border border-line bg-surface px-2 py-1.5 font-mono text-[11px] whitespace-pre text-muted">
                              {i.from}
                            </code>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Panel>
          ))}
        </div>

        {check.data?.note && (
          <p className="mt-4 text-[12px] leading-relaxed text-muted">{check.data.note}</p>
        )}
      </StateView>

      <HealthCheckCadence />
    </div>
  );
}

// ── Module health checks: when each one runs ─────────────────────────────────────────────────
//
// GET /ozpanel/healthchecks is modhealth.CadenceView, json tags exactly. A module that declared what
// changes its health is checked "on change" (its units, its event sources) plus a sweep; the rest
// every pass. POST /ozpanel/healthchecks/sweep is the admin's "check everything now": it marks every
// on-change check due on THIS node and the alerting loop asks them within a minute — so the button
// says that, rather than claiming the checks ran.
interface Cadence {
  module: string;
  mode: string;
  units?: string[] | null;
  sources?: string[] | null;
  blind?: string;
  status: string;
  last_measured_at?: string;
  last_measured_because?: string;
  runs: number;
  reused: number;
  due_now?: string;
  sweep_seconds?: number;
}

const cadenceTone = (s: string) =>
  s === "healthy" ? "ok" : s === "degraded" ? "warn" : s === "critical" || s === "down" ? "bad" : "neutral";

function HealthCheckCadence() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const [busy, setBusy] = React.useState(false);
  const q = useQuery({
    queryKey: ["healthcheck-cadence"],
    queryFn: () => client.apiFetch<{ modules: Cadence[] | null }>("/ozpanel/healthchecks"),
    refetchInterval: 30_000,
  });
  const rows = q.data?.modules ?? [];
  const onChange = rows.filter((r) => r.mode === "on change").length;
  const sweep = rows.find((r) => r.sweep_seconds)?.sweep_seconds;

  const sweepNow = () => {
    setBusy(true);
    void client
      .apiFetch<{ marked: boolean }>("/ozpanel/healthchecks/sweep", { method: "POST" })
      .then(() => {
        notify({
          kind: "success",
          title: "Every on-change health check is due",
          body: "This node's alerting loop asks each of them within a minute; the times below move as they run.",
        });
        void qc.invalidateQueries({ queryKey: ["healthcheck-cadence"] });
      })
      .catch((e: unknown) => notify({ kind: "error", title: "The sweep was not scheduled", body: e instanceof Error ? e.message : String(e) }))
      .finally(() => setBusy(false));
  };

  const columns: DataListColumn<Cadence>[] = [
    { key: "module", label: "Module", render: (r) => <span className="font-mono text-[12px]">{r.module}</span>, value: (r) => r.module },
    { key: "mode", label: "Runs", value: (r) => r.mode },
    {
      key: "status",
      label: "Last result",
      value: (r) => r.status,
      render: (r) => <StatusPill tone={cadenceTone(r.status)}>{r.status}</StatusPill>,
    },
    {
      key: "last",
      label: "Last measured",
      value: (r) => r.last_measured_at ?? "",
      render: (r) =>
        r.last_measured_at ? (
          <span className="text-[12px]">
            {new Date(r.last_measured_at).toLocaleString()}
            {r.last_measured_because && <span className="block text-[11.5px] text-muted">{r.last_measured_because}</span>}
          </span>
        ) : (
          <span className="text-[12px] text-muted">not yet on this node</span>
        ),
    },
    {
      key: "counts",
      label: "Asked / reused",
      value: (r) => r.runs,
      render: (r) =>
        r.mode === "on change" ? (
          <span className="font-mono text-[12px]">
            {r.runs} / {r.reused}
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    { key: "due", label: "Due now", value: (r) => r.due_now ?? "", render: (r) => <span className="text-[12px] text-muted">{r.due_now || ""}</span> },
  ];

  return (
    <Section
      title="Module health checks"
      actions={
        <Button size="sm" variant="ghost" disabled={busy} onClick={sweepNow}>
          {busy ? "Scheduling…" : "Check everything now"}
        </Button>
      }
      bodyClassName="p-0"
      className="mt-6"
    >
      <div className="p-4">
        <p className="mb-3 text-[12px] leading-relaxed text-muted">
          {rows.length > 0 && (
            <>
              {onChange} of {rows.length} modules are checked on change
              {sweep ? `, with a sweep every ${Math.round(sweep / 60)} min` : ""}; the others every pass. On change means:
              when one of the things it declared announces a change, in the sweep, while it is not healthy, and when its
              own page is opened.{" "}
            </>
          )}
          &ldquo;Check everything now&rdquo; makes every on-change check due on this node — the alerting loop asks
          them within a minute. Each node answers for itself.
        </p>
        <DataList
          items={rows}
          columns={columns}
          getRowKey={(r) => r.module}
          loading={q.isLoading}
          error={q.error}
          errorContext="the health check cadence"
          emptyMessage="No module reported a health check."
        />
      </div>
    </Section>
  );
}
